<script lang="ts">
import { PropType, defineComponent, ref, computed } from 'vue'
import { AxiosResponse } from 'axios'
import { Illust, Stamp, StampSummary } from '../../../libs/models'
import { useApi } from '../libs/api'
import { VuemojiPicker, EmojiClickEventDetail } from 'vuemoji-picker'
import Modal from './Modal.vue'
import { useCurrentUserStore } from '../libs/currentUserStore'

export interface StampInfo {
  emoji: string
  code: number
  count: number
  active: boolean
}

export const getStampGroup = (stamps: StampSummary[], ownStamps: Stamp[] = []): StampInfo[] => {
  return stamps
    .map(summary => ({
      emoji: String.fromCodePoint(summary.code),
      code: summary.code,
      count: summary.count,
      active: ownStamps.some(stamp => stamp.code === summary.code)
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5)
}

const DISABLED_EMOJI = [
  '🖕', '👎️', '👊', '🚮', '🚾', '😅', '🤔', '😓', '💢', '💩',
  '☹️', '🙁', '😕', '😮‍💨', '😒', '😐️', '😑', '🤢', '🤮', '🤬',
  '🔪', '🥵', '🤪', '🤑', '🤡', '👽️', '👹', '👺', '😜', '😏',
  '🙄', '🤥', '😤', '😡', '😠', '🤨', '😶', '🫤', '😞', '🥶',
  '😋', '😛', '😝', '😗', '😙', '😩', '😨', '😱']

export default defineComponent({
  components: { VuemojiPicker, Modal },
  props: {
    illust: { type: Object as PropType<Illust>, required: true },
    readonly: { type: Boolean, default: false },
    ownStamps: { type: Array as PropType<Stamp[]>, default: () => [] }
  },
  setup (props) {
    const currentUserStore = useCurrentUserStore()
    const myId = computed(() => currentUserStore.value?.id || NaN)
    const api = useApi()
    const incrementStampSummary = (code: number) => {
      const stampSummary = props.illust.stamps.find(v => v.code === code)
      if (stampSummary) {
        stampSummary.count++
      } else {
        props.illust.stamps.push({ code, count: 1 })
      }
      props.illust.stamps.sort((a, b) => b.count - a.count)
    }
    const decrementStampSummary = (code: number) => {
      const stampSummary = props.illust.stamps.find(v => v.code === code)
      if (!stampSummary) return
      stampSummary.count--
      if (stampSummary.count <= 0) {
        const index = props.illust.stamps.findIndex(v => v.code === code)
        if (index !== -1) props.illust.stamps.splice(index, 1)
      }
      props.illust.stamps.sort((a, b) => b.count - a.count)
    }
    const addStamp = (emoji: string) => {
      const code = emoji.codePointAt(0)
      if (!code) return
      api.post('/stamps', { illust_id: props.illust.id, code }).then(({ data }: AxiosResponse<Stamp>) => {
        props.ownStamps.push(data)
        incrementStampSummary(data.code)
      })
    }
    const removeStamp = (stamp: Stamp) => {
      const index = props.ownStamps.findIndex(v => v.id === stamp.id)
      if (index !== -1) props.ownStamps.splice(index, 1)
      decrementStampSummary(stamp.code)
      api.post(`/stamps/${stamp.id}/delete`)
    }
    const selector = ref(false)
    const handleEmojiClick = (detail: EmojiClickEventDetail) => {
      selector.value = false
      if (!detail.unicode) return
      addStamp(detail.unicode)
    }
    const emojiPicker = (el: any) => {
      if (!el) return
      const style = document.createElement('style')
      style.textContent = `.search-row, .pad-top, ${DISABLED_EMOJI.map(v => `#emo-${v}`).join(', ')} { display: none; }`
      el.picker.shadowRoot.appendChild(style)
    }
    const clickStamp = (stamp: StampInfo) => {
      if (props.readonly) return
      if (!myId.value) return
      const existingStamp = props.ownStamps.find(v => v.code === stamp.code)
      existingStamp ? removeStamp(existingStamp) : addStamp(stamp.emoji)
    }
    const stampGroup = computed(() => getStampGroup(props.illust.stamps, props.ownStamps))
    const canAddNewStamp = computed(() => {
      if (props.readonly) return false
      if (!myId.value) return false
      const ownCodes = Array.from(new Set(props.ownStamps.map(v => v.code)))
      const ownOnlyCount = ownCodes.filter(code => {
        const summary = props.illust.stamps.find(v => v.code === code)
        return summary?.count === 1
      }).length
      return props.illust.stamps.length < 5 && ownOnlyCount < 2
    })
    return {
      myId,
      selector,
      stampGroup,
      handleEmojiClick,
      emojiPicker,
      clickStamp,
      canAddNewStamp
    }
  }
})
</script>

<template>
  <div class="Stamps" :class="{ readonly: readonly || !myId }" @click.stop @scroll.stop @wheel.stop>
    <ul class="Stamps-list">
      <li
        class="Stamps-list-item"
        :class="{ active: stamp.active }"
        @click="clickStamp(stamp)"
        v-for="stamp in stampGroup"
        :key="stamp.code"
      >
        {{ stamp.emoji }}<small v-if="stamp.count >= 2">{{ stamp.count }}</small>
      </li>
      <li class="Stamps-list-item add" v-if="canAddNewStamp" @click="selector = true"><span class="material-symbols-rounded">add_reaction</span></li>
    </ul>
    <transition name="modal">
      <Modal v-if="selector" @close="selector = false">
        <div class="ModalContent">
          <div class="ModalTitle">{{ l('『{title}』にリアクション', { title: illust.title }) }}</div>
          <VuemojiPicker :ref="emojiPicker" @emojiClick="handleEmojiClick" :pickerStyle="{ background: 'none', borderSize: '0' }" />
        </div>
      </Modal>
    </transition>
  </div>
</template>

<style lang="scss">
.Stamps-list {
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: default;
  &-item {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 25px;
    border: 1px solid var(--border-color);
    padding: 1px 4px;
    font-size: 14px;
    border-radius: 20px;
    white-space: nowrap;
    cursor: pointer;
    .readonly & {
      cursor: default;
      border: none;
      &.active {
        border: none;
      }
      &:hover {
        background-color: transparent;
      }
    }
    small {
      margin-left: 2px;
      font-size: 11px;
    }
    &:not(:first-child) {
      margin-left: 3px;
    }
    &:hover {
      background-color: var(--bg-color-selected);
    }
    &.active {
      border-color: var(--orange);
      background-color: rgba(#F70, 0.1);
    }
    &.add {
        padding: 1px 10px 1px 9px;
      .material-symbols-rounded {
        font-variation-settings: 'FILL' 1;
        font-size: 16px;
        color: var(--tx-color-light);
      }
    }
  }
}
</style>
